import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ShowHeader } from "@/common/components/ShowHeader";
import TextField from "@/common/components/TextField";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { SubmitHandler, useForm } from "react-hook-form";
import { objectiveSystemsSchema, ObjectiveSystemsValidationSchema } from "../../schemas/objectiveSystems.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGoBack } from "@/common/hooks/useGoBack";
import { useEffect, useState } from "react";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useQueryClient } from "@tanstack/react-query";
import useMaintenanceObjectiveSystem from "../../hooks/useMaintenanceObjectiveSystem";
import { useParams } from "react-router-dom";
import useUpdateObjectiveSystem from "../../hooks/useUpdateMaintenanceObjectiveSystem";
import { ChevronsRightIcon } from "lucide-react";

const MaintenanceObjectiveSystemsUpdate = () => {
  const { id } = useParams();

  const goBack = useGoBack();

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const {
    data: objectiveSystem,
    isPending: isObjectiveSystemPending,
    refetch: refetchObjectiveSystem,
    error: objectiveSystemError = {} as any,
  } = useMaintenanceObjectiveSystem(id);

  const {
    mutate: updateObjectiveSystem,
    isPending: isUpdateObjectiveSystemPending,
  } = useUpdateObjectiveSystem({
    id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenanceObjectiveSystems'] });
      queryClient.invalidateQueries({ queryKey: ['maintenanceObjectiveSystem', id] });
      goBack({ fallback: `/objective-maintenance/systems/${id}` });
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  })

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<ObjectiveSystemsValidationSchema>({
    resolver: zodResolver(objectiveSystemsSchema),
    defaultValues: {
      display_name: objectiveSystem?.display_name,
    }
  });

  const onSubmit: SubmitHandler<ObjectiveSystemsValidationSchema> = (data) => updateObjectiveSystem(data);

  useEffect(() => {
    if (!isObjectiveSystemPending) {
      reset({
        display_name: objectiveSystem?.display_name,
      })
    }
  }, [isObjectiveSystemPending]);

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-64px)]"
      statusCode={
        hasUserPermission('Admin::Maintenance::ObjectiveSystems::Update') || isUserPermissionsPending
          ? objectiveSystemError?.status
          : 403
      }
      refetchFunction={refetchObjectiveSystem}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ShowHeader
          goBack
          goBackProps={{ fallback: `/objective-maintenance/systems/${id}` }}
          titleClassName="text-nowrap max-w-56 overflow-hidden text-ellipsis font-normal text-base"
          title="Objetos de manutenção"
          subtitle={(
            <div className="flex items-center gap-2">
              <h2 className="text-white-400">
                Sistema
              </h2>

              <ChevronsRightIcon className="text-white-400" />

              <h2 className="text-white-800">
                {objectiveSystem?.display_name}
              </h2>
            </div>
          )}
          isLoading={isObjectiveSystemPending}
          buttons={(
            <div className="flex items-center gap-3">
              <Button
                variant="outline-error"
                onClick={() => goBack({ fallback: `/objective-maintenance/systems/${id}` })}
                disabled={isUpdateObjectiveSystemPending}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                name="submit"
                isLoading={isUpdateObjectiveSystemPending}
                disabled={isObjectiveSystemPending}
              >
                Confirmar
              </Button>
            </div>
          )}
        />

        <Box className="max-h-[calc(100vh-132px)] overflow-y-auto flex flex-col gap-4">
          <p className="text-lg font-medium">
            Dados gerais do sistema
          </p>

          <TextField
            label="Nome do sistema"
            maxLength={32}
            {...register('display_name')}
            disabled={isUpdateObjectiveSystemPending}
            errorMessage={errors.display_name?.message || undefined}
            markAsRequired
          />
        </Box>
      </form>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </ErrorMessageWrapper>
  );
}

export default MaintenanceObjectiveSystemsUpdate;
